import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDate as formatDateGlobal } from '../../../../config/appConfig';
import { useAccCompanies } from '../../companies/useData';
import { useAccPeriods } from '../../periods/useData';
import { defaultPeriodDates } from './reportUtils';
import { useDefaultAccountingPeriod } from './useDefaultAccountingPeriod';

export function useAccountingReportFilters() {
  const defaults = useMemo(() => defaultPeriodDates(), []);

  const { data: companiesRaw, isLoading: companiesLoading } = useAccCompanies('', 1, 500, {
    filters: { filters: { isActive: ['true'] } },
  });
  const companies = useMemo(
    () => [...(companiesRaw ?? [])].sort((a, b) => a.code.localeCompare(b.code)),
    [companiesRaw],
  );

  const { data: periodsRaw } = useAccPeriods('', 1, 100);
  const periods = periodsRaw ?? [];

  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [allCompanies, setAllCompanies] = useState(true);
  const [companyFilter, setCompanyFilter] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const [run, setRun] = useState(false);

  useEffect(() => {
    setSelectedCompanyIds([]);
    setAllCompanies(true);
    setCompanyFilter('');
    setPeriodId('');
    setDateFrom(defaults.from);
    setDateTo(defaults.to);
    setRun(false);
  }, [defaults.from, defaults.to]);

  useEffect(() => {
    if (!periodId) return;
    const period = periods.find((p) => String(p.id) === periodId);
    if (!period) return;
    setDateFrom(period.startDate.slice(0, 10));
    setDateTo(period.endDate.slice(0, 10));
    setRun(false);
  }, [periodId, periods]);

  useDefaultAccountingPeriod(periods, (period) => {
    setPeriodId(String(period.id));
    setDateFrom(period.startDate.slice(0, 10));
    setDateTo(period.endDate.slice(0, 10));
  });

  const filteredCompanies = useMemo(() => {
    const q = companyFilter.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (c) => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q),
    );
  }, [companies, companyFilter]);

  const companyScopeLabel = allCompanies
    ? 'All companies'
    : selectedCompanyIds.length === 0
      ? 'No companies selected'
      : `${selectedCompanyIds.length} selected`;

  const toggleCompany = useCallback((id: number) => {
    setAllCompanies(false);
    setSelectedCompanyIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );
    setRun(false);
  }, []);

  const toggleAllCompanies = useCallback((checked: boolean) => {
    setAllCompanies(checked);
    setSelectedCompanyIds([]);
    setRun(false);
  }, []);

  const periodOptions = useMemo(
    () => [
      { value: '', label: 'Custom period' },
      ...periods.map((p) => ({
        value: String(p.id),
        label: `${p.year} (${formatDateGlobal(p.startDate)} – ${formatDateGlobal(p.endDate)})`,
      })),
    ],
    [periods],
  );

  const companyIdsParam = !allCompanies && selectedCompanyIds.length > 0
    ? selectedCompanyIds.join(',')
    : '';

  const canSearch = Boolean(dateFrom && dateTo && (allCompanies || selectedCompanyIds.length > 0));

  return {
    companies,
    companiesLoading,
    periods,
    periodOptions,
    selectedCompanyIds,
    allCompanies,
    companyFilter,
    setCompanyFilter,
    periodId,
    setPeriodId,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    run,
    setRun,
    filteredCompanies,
    companyScopeLabel,
    toggleCompany,
    toggleAllCompanies,
    companyIdsParam,
    canSearch,
  };
}

export function buildReportQuery(
  basePath: string,
  params: Record<string, string | number | boolean | undefined | null>,
) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}
